import type { ReactNode } from "react";
import { useResizable } from "../../hooks/useResizable";

interface ResizableChatPanelProps {
  isCollapsed: boolean;
  isFullscreen: boolean;
  onClose: () => void;
  onToggleFullscreen: () => void;
  children: ReactNode;
}

export default function ResizableChatPanel({
  isCollapsed,
  isFullscreen,
  onClose,
  onToggleFullscreen,
  children,
}: ResizableChatPanelProps) {
  const { width, handleMouseDown } = useResizable({
    initialWidth: 384,
    minWidth: 280,
    maxWidth: 800,
  });

  if (isCollapsed) return null;

  return (
    <div
      className={`border-l border-gray-200 bg-gray-50 flex flex-col overflow-hidden relative ${
        isFullscreen ? "fixed inset-0 z-50" : ""
      }`}
      style={isFullscreen ? {} : { width: `${width}px` }}
    >
      {/* Resize handle - hidden in fullscreen */}
      {!isFullscreen && (
        <div
          onMouseDown={handleMouseDown}
          className="absolute left-0 top-0 bottom-0 w-1 hover:w-1.5 bg-transparent hover:bg-blue-400 cursor-ew-resize z-40 transition-all"
          style={{ marginLeft: "-2px" }}
        />
      )}

      {/* Header with action buttons */}
      <div className="flex items-center justify-between gap-2 px-3 py-1.5 border-b border-gray-200 bg-white shrink-0">
        <span className="text-sm font-medium text-gray-700">Chat</span>
        
        <div className="flex items-center gap-1.5">
          {/* Expand/Collapse fullscreen button */}
          <button
            onClick={onToggleFullscreen}
            className="w-6 h-6 bg-gray-100 hover:bg-gray-200 text-gray-700 hover:text-gray-900 rounded shadow-sm border border-gray-300 flex items-center justify-center transition-all duration-150"
            title={isFullscreen ? "Exit fullscreen" : "Expand to fullscreen"}
          >
            {isFullscreen ? (
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25"
                />
              </svg>
            ) : (
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
                />
              </svg>
            )}
          </button>

          {/* Close button */}
          <button
            onClick={onClose}
            className="w-6 h-6 bg-gray-100 hover:bg-gray-200 text-gray-700 hover:text-gray-900 rounded shadow-sm border border-gray-300 flex items-center justify-center transition-all duration-150"
            title="Hide chat"
          >
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Chat content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {children}
      </div>
    </div>
  );
}
